// User-facing labels for model enums. American English throughout.

import { termGenderWithReason } from '../model/people';
import type { CitationQuality, GroupKind, MediaKind, NameType, ParentType, PartnerStatus, PartnerType, Person, SiblingType, SocialType, TermStyle, TodoPriority, TodoStatus } from '../model/types';

/** A counted phrase: "1 person", "3 people". */
export function plural(n: number, one: string, many = one + 's'): string {
  return `${n.toLocaleString('en-US')} ${n === 1 ? one : many}`;
}

export const TERM_OPTIONS: { value: TermStyle; label: string }[] = [
  { value: 'auto', label: 'Automatic' },
  { value: 'feminine', label: 'Feminine (mother, aunt, sister)' },
  { value: 'masculine', label: 'Masculine (father, uncle, brother)' },
  { value: 'neutral', label: "Neutral (parent, parent's sibling, sibling)" },
];

/** Why automatic relationship words came out as they did. */
export const TERM_REASONS = { pronouns: 'from their pronouns', gender: 'from their gender', default: 'no pronouns or gender recorded', chosen: '' } as const;

/** The relationship words someone gets, as the editor would describe their choice. */
export function termsDescription(p: Person): string {
  if (p.terms && p.terms !== 'auto') return TERM_OPTIONS.find((o) => o.value === p.terms)!.label;
  const auto = termGenderWithReason({ ...p, terms: 'auto' });
  return `Automatic: ${auto.terms} words${TERM_REASONS[auto.reason] ? `, ${TERM_REASONS[auto.reason]}` : ''}`;
}

export const NAME_TYPE_LABELS: Record<NameType, string> = {
  name: 'Name',
  birth: 'Birth name',
  chosen: 'Chosen name',
  married: 'Married name',
  alias: 'Also known as',
  deadname: 'Deadname',
  other: 'Other',
};

export const PARENT_TYPE_LABELS: Record<ParentType, string> = {
  biological: 'Biological',
  adoptive: 'Adoptive',
  step: 'Step-parent',
  foster: 'Foster',
  guardian: 'Guardian',
  chosen: 'Chosen parent',
  surrogate: 'Surrogate',
  donor: 'Donor',
  unknown: 'Unknown',
};

export const PARTNER_TYPE_LABELS: Record<PartnerType, string> = {
  married: 'Married',
  partner: 'Partners',
  'civil-union': 'Civil union',
  domestic: 'Domestic partnership',
  engaged: 'Engaged',
  dating: 'Dating',
  other: 'Other',
};

export const PARTNER_STATUS_LABELS: Record<PartnerStatus, string> = {
  current: 'Current',
  separated: 'Separated',
  divorced: 'Divorced',
  ended: 'Ended',
  widowed: 'Widowed (partner died)',
};

export const SIBLING_TYPE_LABELS: Record<SiblingType, string> = {
  unknown: 'Sibling',
  full: 'Full sibling',
  half: 'Half-sibling',
  adoptive: 'Adoptive sibling',
  step: 'Stepsibling',
  foster: 'Foster sibling',
  chosen: 'Chosen sibling',
};

export const SOCIAL_TYPE_LABELS: Record<SocialType, string> = {
  friend: 'Friend',
  'close-friend': 'Close friend',
  'best-friend': 'Best friend',
  'family-friend': 'Family friend',
  mentor: 'Mentor',
  roommate: 'Roommate',
  colleague: 'Colleague',
  neighbor: 'Neighbor',
  other: 'Other',
};

export const GROUP_KIND_LABELS: Record<GroupKind, string> = {
  'friend-group': 'Friend group',
  'found-family': 'Found family',
  household: 'Household',
  family: 'Family branch',
  organization: 'Organization / community',
  other: 'Other',
};

export const GROUP_COLORS = ['#7c6cf0', '#0f766e', '#c2410c', '#be185d', '#2563eb', '#65a30d', '#a16207', '#0e7490', '#9333ea', '#dc2626'];

export const TODO_STATUS_LABELS: Record<TodoStatus, string> = {
  open: 'Open',
  'in-progress': 'In progress',
  waiting: 'Waiting',
  done: 'Done',
};

export const TODO_PRIORITY_LABELS: Record<TodoPriority, string> = {
  high: 'High',
  normal: 'Normal',
  low: 'Low',
};

/** Suggestions for a to-do's kind; any text is allowed. */
export const TODO_KIND_SUGGESTIONS = ['Further research', 'Verify', 'Missing events', 'Cleanup', 'Ask a relative', 'Order a record'];

/** GEDCOM QUAY, in words. */
export const QUALITY_LABELS: Record<CitationQuality, string> = {
  3: 'Primary evidence',
  2: 'Secondary evidence',
  1: 'Questionable',
  0: 'Unreliable',
};

export const MEDIA_KIND_LABELS: Record<MediaKind, string> = {
  image: 'Photo',
  pdf: 'PDF',
  audio: 'Audio',
  video: 'Video',
  link: 'Link',
  file: 'File',
};

/** Suggestions for a source's kind; any text is allowed. */
export const SOURCE_KIND_SUGGESTIONS = ['Book', 'Census', 'Certificate', 'Civil record', 'Court record', 'Interview', 'Letter', 'Military record', 'Newspaper', 'Photograph', 'Website'];
